/**
 * useRealtimeSync - SSE-based real-time synchronization hook
 *
 * Establishes a Server-Sent Events connection to receive live updates
 * when media items or posts change on the backend.
 *
 * Features:
 * - Automatic reconnection on disconnect
 * - Exponential backoff for connection retries
 * - Granular React Query cache updates (posts refetch individually)
 * - Connection state management
 * - Unified job tracking (replaces domain-specific events)
 * - Post scheduling and publish progress
 * - Automatic cleanup of stale jobs
 *
 * Usage:
 *   const { isConnected, connectionState } = useRealtimeSync(clientId);
 */

import { useEffect, useRef, useState } from 'react';
import { API_CONFIG } from '@/config/api';
import { useAppStore } from '@/core/store/useAppStore';
import { MediaItem, MediaItemData } from '@/core/MediaItem/MediaItem.model';
import { UserRole } from '@/services/urlService';
import { postCacheUtils } from '@/domains/PostCreator/hooks/usePostsQuery';
import { mediaCacheUtils } from '@/core/MediaLibrary/hooks/useMediaQuery';
import { collectionItemCacheUtils } from '@/core/MediaLibrary/hooks/useCollectionItemsQuery';
import { queryClient } from '@/core/queryClient';
import { ideaKeys } from '@/domains/Ideas/types';
import { adsQueryKeys } from '@/domains/Ads/hooks/useLiveCampaigns';
import { JobType, EntityType, JobStatus } from '@/services/jobsService';

const API_URL = API_CONFIG.URL;

// Cleanup interval for stale jobs (60 seconds)
const JOB_CLEANUP_INTERVAL_MS = 60 * 1000;

// Type definitions for SSE event data
interface JobEventData {
  id: string;
  jobType: JobType;
  entityType: EntityType;
  entityId: string;
  status: JobStatus;
  progress?: number;
  progressMessage?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  resultData?: Record<string, unknown> | null;
}

interface PublishProgressData {
  postId: string;
  scheduledPostId?: string;
  status: 'preparing' | 'prepared' | 'publishing' | 'completed' | 'error';
  platforms?: Record<string, { status: string; error?: string | null }>;
  error?: string;
}

type RealtimeEventType =
  | 'connected'
  // Media events
  | 'media_item_created'
  | 'media_item_updated'
  | 'media_item_deleted'
  // Collection events
  | 'collection_updated'
  // Post events
  | 'post_created'
  | 'post_updated'
  | 'post_deleted'
  // Post scheduling events (workflow events, not jobs)
  | 'post_scheduled'
  | 'post_schedule_cancelled'
  | 'post_publish_progress'
  // Job execution events (unified system for all background processing)
  | 'job_started'
  | 'job_progress'
  | 'job_completed'
  | 'job_failed'
  | 'job_cancelled'
  // Report sync events (background historical sync)
  | 'report_sync_started'
  | 'report_sync_progress'
  | 'report_sync_completed'
  | 'report_sync_failed'
  // Report generation events (async via 202)
  | 'report_gen_complete'
  | 'report_gen_error'
  // AI summary events (async via 202)
  | 'summary_ready'
  | 'summary_error'
  // Follower sync events (async via 202)
  | 'follower_sync_complete'
  | 'follower_sync_error'
  // Audience activity events (async via 202)
  | 'audience_refresh_complete'
  | 'audience_refresh_error'
  // Standalone-ad creation progress (async via 202)
  | 'standalone_ad_progress'
  // Legacy events (deprecated, kept for backwards compatibility)
  | 'caption_generation_started'
  | 'caption_generation_completed'
  | 'image_analysis_started'
  | 'image_analysis_completed'
;

interface RealtimeEvent {
  type: RealtimeEventType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;  // Typed more specifically in each case handler
  timestamp?: string;
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

interface UseRealtimeSyncOptions {
  enabled?: boolean;
  reconnectInterval?: number;  // Base reconnect interval in ms
  maxReconnectInterval?: number;  // Max reconnect interval in ms
  maxReconnectAttempts?: number;  // Max reconnection attempts before giving up
}

// Helper to get access token
const getAccessToken = (): string | null => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    } catch {
      return null;
    }
  }
  return null;
};

export const useRealtimeSync = (
  clientId: string | undefined,
  options: UseRealtimeSyncOptions = {}
) => {
  const {
    enabled = true,
    reconnectInterval = 1000,
    maxReconnectInterval = 30000,
    maxReconnectAttempts = 10,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // Store actions - use getState() to avoid subscribing to the store
  // These are stable function references that don't change
  // Note: Post and media updates now go through React Query, not Zustand
  const storeActions = useRef({
    addToast: useAppStore.getState().addToast,
    setPublishProgress: useAppStore.getState().setPublishProgress,
    clearPublishProgress: useAppStore.getState().clearPublishProgress,
    setStandaloneAdProgress: useAppStore.getState().setStandaloneAdProgress,
    clearStandaloneAdProgress: useAppStore.getState().clearStandaloneAdProgress,
    setJobProgress: useAppStore.getState().setJobProgress,
    cleanupStaleJobs: useAppStore.getState().cleanupStaleJobs,
    updatePostInGrid: useAppStore.getState().updatePostInGrid,
  });

  // Job cleanup interval
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const connect = () => {
    if (!clientId || !enabled) {
      return;
    }

    // Check browser support for EventSource
    if (typeof EventSource === 'undefined') {
      console.error('❌ SSE not supported in this browser');
      setConnectionState('error');
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      console.warn('🔌 No access token available for realtime connection');
      setConnectionState('error');
      return;
    }

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setConnectionState('connecting');

    const url = new URL(`${API_URL}/client/${clientId}/realtime/stream`);
    url.searchParams.append('access_token', accessToken);

    try {
      const eventSource = new EventSource(url.toString());
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('🔌 Realtime connection established');
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;  // Reset reconnect attempts on successful connection
      };

      eventSource.onmessage = (event) => {
        try {
          const data: RealtimeEvent = JSON.parse(event.data);
          // Note: Removed setLastEvent to prevent re-renders on every SSE message

          // Handle different event types
          switch (data.type) {
            case 'connected':
              console.log('✅ Realtime sync connected:', data.data);
              break;

            // Media events - use React Query with granular cache updates
            case 'media_item_created':
            case 'media_item_updated':
              if (data.data) {
                const mediaItem = MediaItem.fromBackend(
                  data.data,
                  UserRole.ADMIN,
                  clientId,
                  accessToken
                );
                // upsertMediaItem handles stage-filtered caches correctly:
                // - Removes items from caches that don't match the item's stage
                // - Adds/updates items in caches that do match
                mediaCacheUtils.upsertMediaItem(clientId, mediaItem);
                console.log(`📸 Media item ${data.type === 'media_item_created' ? 'created' : 'updated'}:`, mediaItem.id);
              }
              break;

            case 'media_item_deleted':
              if (data.data?.id) {
                mediaCacheUtils.removeMediaItem(clientId, data.data.id);
                console.log('🗑️ Media item deleted:', data.data.id);
              }
              break;

            // Collection events - invalidate collection items + idea queries (ideas show linked collections)
            case 'collection_updated':
              if (data.data?.collectionId) {
                collectionItemCacheUtils.invalidateCollectionItems(clientId, data.data.collectionId);
                queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
              }
              break;

            // Post events - use React Query with granular cache updates
            case 'post_created':
            case 'post_updated':
              if (data.data?.id) {
                // Refetch just this post and update it in the cache
                postCacheUtils.refetchAndUpdatePost(clientId, data.data.id);
                // postCacheUtils only touches the FEED cache (["posts", …]).
                // Stories live under their own ["stories", clientId] key, so a
                // story's status/preparation change (e.g. Publish-now promotes
                // it to READY and the server finishes prep) would otherwise not
                // reflect on the Stories board until a manual refresh. Invalidate
                // the stories query so it refetches.
                queryClient.invalidateQueries({ queryKey: ['stories', clientId] });
                console.log(`📝 Post ${data.type === 'post_created' ? 'created' : 'updated'}:`, data.data.id);
              }
              break;

            case 'post_deleted':
              if (data.data?.id) {
                postCacheUtils.removePost(clientId, data.data.id);
                console.log('🗑️ Post deleted:', data.data.id);
              }
              break;

            // Report sync events (background historical sync)
            case 'report_sync_started':
              console.log('Sync started:', data.data?.syncId);
              break;

            case 'report_sync_progress':
              console.log(
                'Sync progress:',
                data.data?.synced, '/', data.data?.total,
                data.data?.currentMonth,
              );
              break;

            case 'report_sync_completed':
              storeActions.current.addToast({
                message: `Report sync complete: ${data.data?.synced ?? 0} months synced`,
                type: 'success',
                duration: 5000,
              });
              break;

            case 'report_sync_failed':
              storeActions.current.addToast({
                message: data.data?.error || 'Report sync failed',
                type: 'error',
                duration: 5000,
              });
              break;

            // Async report generation events
            case 'report_gen_complete':
              console.log('Report generation complete:', data.data?.genId);
              storeActions.current.addToast({
                message: 'Report generated',
                type: 'success',
                duration: 3000,
              });
              break;

            case 'report_gen_error':
              storeActions.current.addToast({
                message: data.data?.error || 'Report generation failed',
                type: 'error',
                duration: 5000,
              });
              break;

            // Async AI summary events
            case 'summary_ready':
              console.log('Summary ready:', data.data?.genId);
              break;

            case 'summary_error':
              storeActions.current.addToast({
                message: data.data?.error || 'Summary generation failed',
                type: 'error',
                duration: 5000,
              });
              break;

            // Async follower sync events
            case 'follower_sync_complete':
              storeActions.current.addToast({
                message: `Follower sync complete: ${data.data?.synced ?? 0} clients synced`,
                type: 'success',
                duration: 5000,
              });
              break;

            case 'follower_sync_error':
              storeActions.current.addToast({
                message: data.data?.error || 'Follower sync failed',
                type: 'error',
                duration: 5000,
              });
              break;

            // Async audience activity events
            case 'audience_refresh_complete':
              console.log('Audience refresh complete:', data.data?.syncId);
              break;

            case 'audience_refresh_error':
              storeActions.current.addToast({
                message: data.data?.error || 'Audience refresh failed',
                type: 'error',
                duration: 5000,
              });
              break;

            case 'standalone_ad_progress': {
              const saPayload = data.data as {
                postId?: string;
                taskId?: string;
                phase?: string;
                message?: string;
                error?: string;
                campaignId?: string | null;
              } | undefined;
              const saPostId = saPayload?.postId;
              const saPhase = saPayload?.phase;
              if (!saPostId || !saPhase) break;

              // Update progress store so the post card can render a badge.
              storeActions.current.setStandaloneAdProgress(saPostId, {
                postId: saPostId,
                taskId: saPayload?.taskId || "",
                phase: saPhase as
                  | 'queued'
                  | 'preparing'
                  | 'uploading_creative'
                  | 'creating_campaign'
                  | 'completed'
                  | 'failed',
                message: saPayload?.message,
                error: saPayload?.error,
                campaignId: saPayload?.campaignId ?? null,
              });

              if (saPhase === 'queued') {
                storeActions.current.addToast({
                  message: saPayload?.message || 'Standalone ad queued.',
                  type: 'info',
                  duration: 3000,
                });
              } else if (saPhase === 'completed') {
                storeActions.current.addToast({
                  message: saPayload?.message || 'Standalone ad is live.',
                  type: 'success',
                  duration: 5000,
                });
                // Invalidate the live-campaigns query so SponsoredBadge
                // picks up the new campaign without a page reload. SSE
                // tells us the campaign went ACTIVE on Meta; the
                // frontend's cached campaigns list is stale until
                // refetched.
                if (clientId) {
                  queryClient.invalidateQueries({
                    queryKey: adsQueryKeys.liveCampaigns(clientId),
                  });
                }
                // Clear the in-progress badge after a short delay so
                // the "completed" state stays visible briefly.
                setTimeout(() => {
                  storeActions.current.clearStandaloneAdProgress(saPostId);
                }, 4000);
              } else if (saPhase === 'failed') {
                storeActions.current.addToast({
                  message: saPayload?.message || saPayload?.error || 'Standalone ad failed.',
                  type: 'error',
                  duration: 15_000,
                });
                // Deliberately NOT auto-clearing failed state. The
                // post card keeps the red "Ad failed" pill until the
                // user explicitly dismisses or retries — otherwise
                // the error flashes by unread and the user has no
                // way to recover context.
              }
              // preparing / uploading_creative / creating_campaign:
              // silent progress — no toast, badge on post card shows
              // the phase label.
              break;
            }

            // Legacy AI Processing Events (deprecated - use job_* events instead)
            // Kept for backwards compatibility during migration period
            case 'caption_generation_started':
            case 'caption_generation_completed':
            case 'image_analysis_started':
            case 'image_analysis_completed':
            // Post Scheduling Events
            // Note: Backend also emits post_updated after scheduling, which triggers refetch
            case 'post_scheduled':
              if (data.data?.postId) {
                const action = data.data.isReschedule ? 'rescheduled' : 'scheduled';
                console.log(`📅 Post ${action}:`, data.data.postId);
                storeActions.current.addToast({
                  message: `Post ${action} successfully`,
                  type: 'success',
                  duration: 3000,
                });
              }
              break;

            case 'post_schedule_cancelled':
              if (data.data?.postId) {
                console.log('📅 Post schedule cancelled:', data.data.postId);
                storeActions.current.addToast({
                  message: 'Post schedule cancelled',
                  type: 'info',
                  duration: 3000,
                });
              }
              break;

            case 'post_publish_progress':
              if (data.data?.postId) {
                const postId = data.data.postId;
                const status = data.data.status;
                console.log('📤 Post publish progress:', postId, 'status:', status);
                storeActions.current.setPublishProgress(postId, {
                  postId: postId,
                  scheduledPostId: data.data.scheduledPostId,
                  status: status,
                  platforms: data.data.platforms,
                  error: data.data.error,
                });

                // Show toast for terminal states
                if (status === 'completed') {
                  storeActions.current.addToast({
                    message: 'Post published successfully!',
                    type: 'success',
                    duration: 5000,
                  });
                } else if (status === 'error') {
                  storeActions.current.addToast({
                    message: data.data.error || 'Post publishing failed',
                    type: 'error',
                    duration: 5000,
                  });
                } else if (status === 'partial') {
                  storeActions.current.addToast({
                    message: 'Post partially published - some platforms failed',
                    type: 'warning',
                    duration: 5000,
                  });
                }

                // Clear progress after successful terminal states with delay
                // for UI feedback. 'error' is deliberately NOT auto-cleared
                // (mirrors the standalone-ad handling): the post card keeps a
                // red "Publish failed" pill until the state changes, otherwise
                // the failure vanishes after the toast and the card silently
                // shows its stale scheduled badge.
                if (status === 'completed' || status === 'partial') {
                  setTimeout(() => {
                    storeActions.current.clearPublishProgress(postId);
                  }, 3000);
                }
              }
              break;

            // Job Execution Events - centralized job tracking via SSE
            case 'job_started':
            case 'job_progress':
              if (data.data?.id) {
                console.log(`🔧 Job ${data.type === 'job_started' ? 'started' : 'progress'}:`, data.data.id, data.data.progressMessage || '');
                storeActions.current.setJobProgress({
                  id: data.data.id,
                  jobType: data.data.jobType,
                  entityType: data.data.entityType,
                  entityId: data.data.entityId,
                  status: data.data.status,
                  progress: data.data.progress ?? 0,
                  progressMessage: data.data.progressMessage ?? null,
                });
              }
              break;

            case 'job_completed':
              if (data.data?.id) {
                console.log('✅ Job completed:', data.data.id);
                storeActions.current.setJobProgress({
                  id: data.data.id,
                  jobType: data.data.jobType,
                  entityType: data.data.entityType,
                  entityId: data.data.entityId,
                  status: 'completed',
                  progress: 100,
                  progressMessage: null,
                  resultData: data.data.resultData,
                });
              }
              break;

            case 'job_failed':
              if (data.data?.id) {
                console.log('❌ Job failed:', data.data.id, data.data.errorMessage);
                storeActions.current.setJobProgress({
                  id: data.data.id,
                  jobType: data.data.jobType,
                  entityType: data.data.entityType,
                  entityId: data.data.entityId,
                  status: 'failed',
                  progress: data.data.progress ?? 0,
                  progressMessage: null,
                  errorCode: data.data.errorCode,
                  errorMessage: data.data.errorMessage,
                });
              }
              break;

            case 'job_cancelled':
              if (data.data?.id) {
                console.log('🚫 Job cancelled:', data.data.id);
                storeActions.current.setJobProgress({
                  id: data.data.id,
                  jobType: data.data.jobType,
                  entityType: data.data.entityType,
                  entityId: data.data.entityId,
                  status: 'cancelled',
                  progress: data.data.progress ?? 0,
                  progressMessage: null,
                  errorMessage: data.data.errorMessage,
                });
              }
              break;

            default:
              console.log('📡 Realtime event:', data.type, data.data);
          }
        } catch (error) {
          console.error('❌ Error processing realtime event:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('❌ Realtime connection error:', error);
        setConnectionState('error');

        // Clean up and attempt reconnect
        eventSource.close();
        eventSourceRef.current = null;

        // Check if max reconnection attempts reached
        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error(`❌ Max reconnection attempts (${maxReconnectAttempts}) reached, giving up`);
          console.warn('⚠️ Realtime sync disabled. Reload page to reconnect.');
          setConnectionState('error');
          return;
        }

        // Exponential backoff for reconnection
        reconnectAttemptsRef.current += 1;
        const backoffTime = Math.min(
          reconnectInterval * Math.pow(2, reconnectAttemptsRef.current - 1),
          maxReconnectInterval
        );

        console.log(`🔄 Reconnecting in ${backoffTime}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, backoffTime);
      };
    } catch (error) {
      console.error('❌ Failed to establish realtime connection:', error);
      setConnectionState('error');
    }
  };

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setConnectionState('disconnected');
    console.log('🔌 Realtime connection closed');
  };

  // Connect on mount, disconnect on unmount.
  // Also gate on document visibility so a backgrounded tab doesn't pin
  // a Cloud Run instance alive with a long-lived SSE request.
  useEffect(() => {
    if (!enabled || !clientId) {
      return;
    }

    if (typeof document === 'undefined' || !document.hidden) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [clientId, enabled]);

  // Pause SSE when tab is hidden, resume when visible.
  useEffect(() => {
    if (!enabled || !clientId || typeof document === 'undefined') {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (eventSourceRef.current || reconnectTimeoutRef.current) {
          console.log('🔌 Tab hidden - closing realtime connection');
          disconnect();
        }
      } else if (!eventSourceRef.current) {
        console.log('🔌 Tab visible - reopening realtime connection');
        reconnectAttemptsRef.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clientId, enabled]);

  // Reconnect when coming back online
  useEffect(() => {
    const handleOnline = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      console.log('🌐 Network connection restored, reconnecting...');
      connect();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [clientId]);

  // Job cleanup interval - remove stale completed/failed jobs
  useEffect(() => {
    // Start cleanup interval
    cleanupIntervalRef.current = setInterval(() => {
      storeActions.current.cleanupStaleJobs();
    }, JOB_CLEANUP_INTERVAL_MS);

    // Run cleanup once on mount
    storeActions.current.cleanupStaleJobs();

    return () => {
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
        cleanupIntervalRef.current = null;
      }
    };
  }, []);

  return {
    isConnected: connectionState === 'connected',
    connectionState,
    reconnect: connect,
    disconnect,
  };
};
